import * as Sentry from '@sentry/react';
import type { AxiosError } from 'axios';
import {
  captureSentryEvent,
  setSentryTag,
  setSentryContext,
} from '@/config/sentry';

const SENSITIVE_KEYS = [
  'password',
  'newpassword',
  'oldpassword',
  'token',
  'accesstoken',
  'refreshtoken',
  'authorization',
  'cookie',
  'otp',
  'secret',
  'apikey',
  'privatekey',
];

function sanitize(value: unknown, depth = 0): unknown {
  if (depth > 4 || value == null) return value;
  if (typeof value !== 'object') return value;
  if (Array.isArray(value)) return value.map(v => sanitize(v, depth + 1));
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    out[k] = SENSITIVE_KEYS.includes(k.toLowerCase())
      ? '[REDACTED]'
      : sanitize(v, depth + 1);
  }
  return out;
}

function truncate(value: unknown, max = 500): unknown {
  if (typeof value !== 'string') return value;
  return value.length > max ? `${value.slice(0, max)}…[truncated]` : value;
}

// Service for centralized Sentry operations
export class SentryService {
  // Track API errors
  static trackApiError(
    endpoint: string,
    method: string,
    error: unknown,
    status?: number,
  ) {
    // Skip tracking expected HTTP errors
    // 401 = Unauthorized (session expired, not logged in - expected)
    // 403 = Forbidden (user doesn't have permission - expected)
    // 404 = Not Found (resource doesn't exist - expected)
    if (status && [401, 403, 404].includes(status)) {
      return; // Don't track these expected errors
    }

    if (import.meta.env.MODE !== 'production') return;

    const axiosErr = error as AxiosError | undefined;
    const requestBody = axiosErr?.config?.data;
    const responseBody = axiosErr?.response?.data;
    const requestId =
      (axiosErr?.response?.headers?.['x-request-id'] as string | undefined) ??
      (axiosErr?.response?.headers?.['x-correlation-id'] as string | undefined);

    let parsedRequestBody: unknown = requestBody;
    if (typeof requestBody === 'string') {
      try {
        parsedRequestBody = JSON.parse(requestBody);
      } catch {
        parsedRequestBody = truncate(requestBody);
      }
    }

    const apiContext = {
      endpoint,
      method: method.toUpperCase(),
      status: status ?? 0,
      requestId,
      requestBody: sanitize(parsedRequestBody),
      responseBody: truncate(
        typeof responseBody === 'string'
          ? responseBody
          : JSON.stringify(sanitize(responseBody) ?? null),
        500,
      ),
      timestamp: new Date().toISOString(),
    };

    Sentry.withScope(scope => {
      scope.setTag('api.endpoint', endpoint);
      scope.setTag('api.method', method.toUpperCase());
      scope.setTag('api.status', String(status ?? 0));
      if (requestId) scope.setTag('api.request_id', requestId);
      scope.setContext('api', apiContext);
      scope.setFingerprint([
        'api-error',
        method.toUpperCase(),
        endpoint,
        String(status ?? 0),
      ]);
      if (error instanceof Error) {
        Sentry.captureException(error);
      } else {
        Sentry.captureMessage(`API Error: ${method} ${endpoint}`, 'error');
      }
    });
  }

  // Track authentication errors
  static trackAuthError(action: string, error: unknown, userId?: string) {
    const errorContext = {
      action,
      userId,
      error: error instanceof Error ? error.message : String(error),
      timestamp: new Date().toISOString(),
    };

    setSentryContext('auth_error', errorContext);
    captureSentryEvent(
      `Authentication Error: ${action}`,
      'error',
      errorContext,
    );
  }

  // Track wallet operations
  static trackWalletError(
    operation: string,
    error: unknown,
    walletType?: string,
  ) {
    const errorContext = {
      operation,
      walletType,
      error: error instanceof Error ? error.message : String(error),
      timestamp: new Date().toISOString(),
    };

    setSentryContext('wallet_error', errorContext);
    captureSentryEvent(`Wallet Error: ${operation}`, 'error', errorContext);
  }

  // Track tournament operations
  static trackTournamentError(
    operation: string,
    tournamentId: string,
    error: unknown,
  ) {
    const errorContext = {
      operation,
      tournamentId,
      error: error instanceof Error ? error.message : String(error),
      timestamp: new Date().toISOString(),
    };

    setSentryContext('tournament_error', errorContext);
    captureSentryEvent(`Tournament Error: ${operation}`, 'error', errorContext);
  }

  // Track user actions
  static trackUserAction(action: string, data?: Record<string, unknown>) {
    Sentry.addBreadcrumb({
      message: `User action: ${action}`,
      category: 'user',
      level: 'info',
      data,
    });
  }

  // Track navigation
  static trackNavigation(
    from: string,
    to: string,
    method: string = 'navigation',
  ) {
    Sentry.addBreadcrumb({
      message: `Navigation: ${from} → ${to}`,
      category: 'navigation',
      level: 'info',
      data: { from, to, method },
    });
  }

  // Track performance metrics
  static trackPerformance(
    operation: string,
    duration: number,
    metadata?: Record<string, unknown>,
  ) {
    Sentry.addBreadcrumb({
      message: `Performance: ${operation}`,
      category: 'performance',
      level: 'info',
      data: {
        operation,
        duration,
        ...metadata,
      },
    });
  }

  // Set feature flags context
  static setFeatureFlags(flags: Record<string, boolean>) {
    setSentryContext('feature_flags', flags);
  }

  // Set environment context
  static setEnvironment(env: string) {
    setSentryTag('environment', env);
  }

  // Set release version
  static setRelease(version: string) {
    setSentryTag('release', version);
  }

  // Track custom business events
  static trackBusinessEvent(event: string, data?: Record<string, unknown>) {
    captureSentryEvent(`Business Event: ${event}`, 'info', data);
  }

  // Track critical user flows
  static trackUserFlow(
    flow: string,
    step: string,
    data?: Record<string, unknown>,
  ) {
    Sentry.addBreadcrumb({
      message: `User Flow: ${flow} - ${step}`,
      category: 'user_flow',
      level: 'info',
      data: {
        flow,
        step,
        ...data,
      },
    });
  }
}
